import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Move3d, MousePointer2, Sparkles } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
    "https://images.unsplash.com/photo-1760072513357-9d450e935a80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBpbnRlcmlvciUyMGFyY2hpdGVjdHVyZSUyMDM2MCUyMHBhbm9yYW1hJTIwaG90ZWwlMjBzaG93cm9vbSUyMHJlYWwlMjBlc3RhdGV8ZW58MHx8fHwxNzg5MDI2MTY5fDA&ixlib=rb-4.1.0&q=85&w=2200&h=1100&fit=crop";

const LINES = [
    { text: "DON'T JUST SHOW", accent: false },
    { text: "YOUR SPACE.", accent: false },
    { text: "LET THEM", accent: true },
    { text: "EXPERIENCE IT.", accent: true },
];

export default function Hero() {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 40, damping: 18 });
    const sy = useSpring(my, { stiffness: 40, damping: 18 });
    const bgX = useTransform(sx, [-0.5, 0.5], [22, -22]);
    const bgY = useTransform(sy, [-0.5, 0.5], [14, -14]);
    const cardX = useTransform(sx, [-0.5, 0.5], [-16, 16]);
    const cardY = useTransform(sy, [-0.5, 0.5], [-10, 10]);

    const onMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            id="hero"
            ref={ref}
            onMouseMove={onMove}
            data-testid="hero-section"
            className="noise-overlay relative flex min-h-screen items-center overflow-hidden"
        >
            <motion.div className="absolute inset-[-40px]" style={{ x: bgX, y: bgY }}>
                <motion.img
                    src={HERO_IMG}
                    alt="Luxury interior 360 panorama"
                    initial={{ scale: 1.18 }}
                    animate={{ scale: 1.04 }}
                    transition={{ duration: 6, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full w-full object-cover opacity-45"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-[#050B14]/80 via-[#050B14]/40 to-[#050B14]" />
            <div className="grid-lines absolute inset-0 opacity-60" />
            <div className="absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-[#00C6FF]/15 blur-[130px]" />
            <div className="absolute -right-32 bottom-1/4 h-80 w-80 rounded-full bg-[#0072FF]/15 blur-[120px]" />

            <motion.div
                style={{ x: cardX, y: cardY }}
                className="pointer-events-none absolute right-[9%] top-[15%] hidden xl:block"
                data-testid="hero-orbit-dial"
            >
                <div className="relative flex h-48 w-48 items-center justify-center">
                    <svg viewBox="0 0 200 200" className="orbit-ring absolute inset-0 h-full w-full">
                        <defs>
                            <path
                                id="dial-circle"
                                d="M100,100 m-80,0 a80,80 0 1,1 160,0 a80,80 0 1,1 -160,0"
                            />
                        </defs>
                        <text
                            className="fill-[#00C6FF]"
                            style={{ fontSize: "12.5px", letterSpacing: "4.5px", fontFamily: "'JetBrains Mono', monospace" }}
                        >
                            <textPath href="#dial-circle">
                                EXPERIENCE EVERY ANGLE • PRIME 360° VIEW •
                            </textPath>
                        </text>
                    </svg>
                    <div
                        className="absolute inset-7 rounded-full border border-dashed border-[#00C6FF]/35"
                        style={{ animation: "spin-slow 14s linear infinite reverse" }}
                    />
                    <div
                        className="absolute inset-11 rounded-full border border-[#00C6FF]/20"
                        style={{ animation: "spin-slow 40s linear infinite" }}
                    />
                    <div className="cta-glow flex h-[72px] w-[72px] items-center justify-center rounded-full border border-[#00C6FF]/60 bg-[#0A1F44]/70 backdrop-blur-md">
                        <span className="font-mono-accent text-base font-bold text-[#00C6FF]">
                            360°
                        </span>
                    </div>
                </div>
            </motion.div>

            <motion.div
                style={{ x: cardX, y: cardY }}
                className="pointer-events-none absolute bottom-[16%] right-[16%] hidden xl:block"
            >
                <div className="glass-card float-slow rounded-2xl px-6 py-5" style={{ animationDelay: "1.4s" }}>
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#00C6FF]/50 text-[#00C6FF]">
                            <MousePointer2 size={20} />
                        </span>
                        <div>
                            <p className="font-mono-accent text-[10px] tracking-[0.3em] text-[#00C6FF]">
                                PUNE · INDIA
                            </p>
                            <p className="text-sm font-semibold text-white">
                                8K HDR Capture Studio
                            </p>
                        </div>
                    </div>
                </div>
            </motion.div>

            <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-36 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: -16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#00C6FF]/35 bg-[#0A1F44]/50 px-5 py-2.5 backdrop-blur-md"
                    data-testid="hero-price-badge"
                >
                    <Sparkles size={14} className="text-[#00C6FF]" />
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-200 sm:text-sm">
                        360° Experiences Starting From{" "}
                        <span className="text-[#00C6FF]">₹4,999</span>
                    </span>
                </motion.div>

                <h1 className="max-w-5xl text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
                    {LINES.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className={`block ${
                                    line.accent
                                        ? "text-glow bg-gradient-to-r from-[#00C6FF] via-[#5fdcff] to-[#0072FF] bg-clip-text text-transparent"
                                        : "text-white"
                                }`}
                                initial={{ y: "115%" }}
                                animate={{ y: 0 }}
                                transition={{
                                    duration: 1.05,
                                    delay: 0.35 + i * 0.13,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                            >
                                {line.text}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.0 }}
                    className="mt-7 max-w-xl text-base text-slate-300 sm:text-lg"
                >
                    Your space deserves more than flat photos. We turn it into an
                    immersive 360° experience customers can wander through at midnight,
                    from miles away — long before they ever visit.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.15 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-explore-btn"
                        onClick={() => scrollToId("vision")}
                        className="cta-glow rounded-full bg-[#00C6FF] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] text-[#050B14] transition-transform duration-300 hover:scale-105"
                    >
                        Explore 360°
                    </button>
                    <button
                        data-testid="hero-quote-btn"
                        onClick={() => scrollToId("contact")}
                        className="rounded-full border border-[#00C6FF]/50 px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] text-[#00C6FF] backdrop-blur-md transition-colors duration-300 hover:bg-[#00C6FF]/10"
                    >
                        Get Custom Quote
                    </button>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
            >
                <div className="flex h-12 w-7 items-start justify-center rounded-full border border-[#00C6FF]/40 p-1.5">
                    <motion.div
                        animate={{ y: [0, 18, 0] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                        className="h-2 w-2 rounded-full bg-[#00C6FF]"
                    />
                </div>
            </motion.div>
        </section>
    );
}
